import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { loadUsers } from "reduxSlices/organizationUsersSlice"
import types from "prop-types"
import * as API from "services/api"
import { groupAudit } from "utils/auditHelper"
import { errorToast } from "shared/toast"
import UserActionAuditTrailContext from "../UserActionAuditTrailContext"

const UserActionAuditTrailContextProvider = ({ children, userId }) => {
  const dispatch = useDispatch()
  const [auditList, setAuditList] = useState([])
  const [cursor, setCursor] = useState(null)
  const [initialAuditsLoaded, setInitialAuditsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [allAuditsLoaded, setAllAuditsLoaded] = useState(false)

  const fetchAudits = async () => {
    if (!userId) return

    setIsLoading(true)

    const response = await API.getUserActionAudits({ userId, cursor })
    setIsLoading(false)

    if (response.ok) {
      if (response.data.length === 0) {
        setAllAuditsLoaded(true)
      } else {
        const latestAuditId = response.data.at(-1)?.id
        setCursor(latestAuditId)
        setAuditList(auditList.concat(response.data))
      }
    } else {
      errorToast("Something went wrong. Unable to load the user's activity.")
    }
  }

  useEffect(() => {
    const initializeStateForUser = async () => {
      const auditsResponse = await API.getUserActionAudits({ userId })
      setIsLoading(false)

      if (auditsResponse.ok) {
        setInitialAuditsLoaded(true)
        setAllAuditsLoaded(auditsResponse.data.length === 0)
        setAuditList(auditsResponse.data)

        const latestAuditId = auditsResponse.data.at(-1)?.id
        setCursor(latestAuditId)
      } else {
        errorToast("Something went wrong. Unable to load the user's activity.")
      }
    }

    initializeStateForUser()
  }, [userId])

  // Load organization users
  useEffect(() => {
    const load = async () => {
      const response = await API.getUsers()

      if (response.ok) {
        return response.data
      }

      throw new Error("Unable to load users.")
    }

    dispatch(loadUsers({ load }))
  }, [dispatch])

  const contextConsumerValue = {
    allAuditsLoaded,
    initialAuditsLoaded,
    auditGroups: groupAudit(auditList),
    fetchAudits,
    isLoading,
  }

  return (
    <UserActionAuditTrailContext.Provider value={contextConsumerValue}>
      {children}
    </UserActionAuditTrailContext.Provider>
  )
}

UserActionAuditTrailContextProvider.propTypes = {
  children: types.node.isRequired,
  userId: types.number.isRequired,
}

export default UserActionAuditTrailContextProvider
